import React, { useState } from "react";
import {
  TextField,
  Button,
  InputAdornment,
  List,
  ListItem,
  Avatar,
  ListItemText,
  Paper,
  Box,
  Pagination,
  Divider,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import axios from "axios";
import { useTranslate } from "@refinedev/core";
import { getToken } from "../../utils/auth-storage";
import { UserSearchProps, UserSearchResult } from "../../interfaces/search";
import axiosInstance from "../../api/axios-config";

interface ValidationResult {
  isValid: boolean;
  type?: "email" | "phone" | "name";
  error?: string;
}

export const UserSearch: React.FC<
  UserSearchProps & {
    addedUserIds?: number[];
    addedContacts?: {
      emails: string[];
      phones: string[];
    };
  }
> = ({
  onUserSelect,
  onManualAdd,
  inputLabel,
  apiUrl,
  showManualAdd = false,
  addedUserIds = [],
  addedContacts = { emails: [], phones: [] },
  ...rest
}) => {
  const translate = useTranslate();
  const resolvedInputLabel = inputLabel ?? translate('search.inputLabel');
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const ITEMS_PER_PAGE = 5;

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone: string) => {
    // More lenient phone validation that matches backend
    return /^[+]?[\d\s-]{8,}$/.test(phone.trim());
  };

  const validateSearchInput = (input: string): ValidationResult => {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
      return {
        isValid: false,
        error: "Please enter an email or phone number to search",
      };
    }
    if (isValidEmail(trimmedInput)) {
      return { isValid: true, type: "email" };
    }
    if (isValidPhone(trimmedInput)) {
      return { isValid: true, type: "phone" };
    }
    if (trimmedInput.length >= 3) {
      return { isValid: true, type: "name" };
    }
    return {
      isValid: false,
      error: "Please enter at least 3 characters for name search",
    };
  };

  const handleSearch = async () => {
    const validation = validateSearchInput(searchInput);
    if (!validation.isValid) {
      setInputError(validation.error || "Invalid input");
      return;
    }

    setInputError(null);
    setIsSearching(true);

    try {
      const baseApiUrl =
        apiUrl || localStorage.getItem("API_URL");
      const authToken = getToken();

      if (!authToken) {
        setInputError("Authentication error. Please login again.");
        setIsSearching(false);
        return;
      }

      const cleanInput = searchInput.trim();

      try {
        const response = await axiosInstance.post(
          `${baseApiUrl}/users/search`,
          { search: cleanInput },
        );

        if (response.data) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            const usersData = response.data.map((userData) => {
              if (
                userData.profilePicture &&
                !userData.profilePicture.startsWith("http")
              ) {
                userData.profilePicture = `${baseApiUrl}/uploads/${userData.profilePicture}`;
              }
              return userData;
            });
            setSearchResults(usersData);
            setTotalPages(Math.ceil(usersData.length / ITEMS_PER_PAGE));
            setCurrentPage(1);
            setIsSearching(false);
            return;
          } else if (!Array.isArray(response.data)) {
            const userData = response.data;
            if (
              userData.profilePicture &&
              !userData.profilePicture.startsWith("http")
            ) {
              userData.profilePicture = `${baseApiUrl}/uploads/${userData.profilePicture}`;
            }
            setSearchResults([userData]);
            setTotalPages(1);
            setCurrentPage(1);
            setIsSearching(false);
            return;
          }
        }
      } catch (error) {
        console.log("POST search failed, trying GET method");
      }

      // Only try GET method if the input is an email or phone
      if (validation.type === "email" || validation.type === "phone") {
        try {
          const response = await axiosInstance.get(`${baseApiUrl}/users/search`, {
            params: { contact: cleanInput },
          });

          if (response.data) {
            const userData = response.data;
            if (
              userData.profilePicture &&
              !userData.profilePicture.startsWith("http")
            ) {
              userData.profilePicture = `${baseApiUrl}/uploads/${userData.profilePicture}`;
            }
            setSearchResults([userData]);
            setTotalPages(1);
            setIsSearching(false);
            return;
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 400) {
              setInputError("Invalid format. Please check your input.");
              setIsSearching(false);
              return;
            }
          }
        }
      }

      // Only try check endpoint for email/phone
      if (validation.type === "email" || validation.type === "phone") {
        try {
          const checkResponse = await axiosInstance.get(`${baseApiUrl}/users/check`, {
            params: { [validation.type]: cleanInput },
            headers: { Authorization: `Bearer ${authToken}` },
          });

          if (checkResponse.data.exists) {
            const minimalUser = {
              id: Date.now(),
              [validation.type]: cleanInput,
              email: validation.type === "email" ? cleanInput : "",
              phone: validation.type === "phone" ? cleanInput : "",
              fullname: "User Found",
            };
            setSearchResults([minimalUser]);
            setTotalPages(1);
            setIsSearching(false);
            return;
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            console.error("Check endpoint failed:", error);
          }
        }
      }

      setSearchResults([]);
    } catch (error) {
      console.error("Error searching for users:", error);
      setInputError(
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : "Error searching for users"
      );
      setSearchResults([]);
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleClear = () => {
    setSearchInput("");
    setSearchResults([]);
    setInputError(null);
    setHasSearched(false);
  };

  const handleManualEntry = () => {
    if (!searchInput.trim()) return;

    let type: "email" | "phone";
    if (isValidEmail(searchInput)) {
      type = "email";
    } else if (isValidPhone(searchInput)) {
      type = "phone";
    } else {
      setInputError("Please enter a valid email or phone number");
      return;
    }

    if (onManualAdd) {
      onManualAdd(searchInput, type);
      setSearchInput("");
      setSearchResults([]);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return searchResults.slice(startIndex, endIndex);
  };

  // Check if a user has already been added to the security list
  const isUserAlreadyAdded = (user: UserSearchResult) => {
    // Check by ID first
    if (addedUserIds.includes(user.id)) {
      return true;
    }

    // Then check by email and phone
    if (user.email && addedContacts.emails.includes(user.email)) {
      return true;
    }

    if (user.phone && addedContacts.phones.includes(user.phone)) {
      return true;
    }

    return false;
  };

  return (
    <Box>
      <TextField
        label={inputLabel}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        error={!!inputError}
        helperText={inputError}
        fullWidth
        margin="normal"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  disabled={isSearching}
                >
                  {isSearching ? translate('search.searching') : translate('search.search')}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClear}
                  startIcon={<ClearIcon />}
                  disabled={
                    isSearching || (!searchInput && searchResults.length === 0)
                  }
                >
                  {translate('search.clear')}
                </Button>
              </Box>
            </InputAdornment>
          ),
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSearch();
          }
        }}
      />

      {isSearching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      ) : searchResults.length > 0 ? (
        <Paper variant="outlined" sx={{ mt: 2 }}>
          <List dense>
            {getCurrentPageItems().map((user, index) => (
              <React.Fragment key={user.id || index}>
                <ListItem>
                  <Avatar sx={{ mr: 2 }} src={user.profilePicture}>
                    {(user.fullname || "U")[0]}
                  </Avatar>
                  <ListItemText
                    primary={user.fullname || "Unnamed User"}
                    // Use a simple string instead of Typography for secondary text
                    secondary={`${user.email || ""} ${
                      user.email && user.phone ? "•" : ""
                    } ${user.phone || ""}`}
                  />
                  {isUserAlreadyAdded(user) ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled
                    >
                      Added
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => onUserSelect(user)}
                    >
                      Add
                    </Button>
                  )}
                </ListItem>
                {index < getCurrentPageItems().length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      ) : hasSearched && searchInput && !isSearching ? (
        // Use span to avoid nesting typography elements
        <Box sx={{ mt: 1, mb: 1, color: "text.secondary" }}>
          No users found matching your search.
        </Box>
      ) : null}

      {!isSearching &&
        hasSearched &&
        searchResults.length === 0 &&
        searchInput.trim() !== "" && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleManualEntry}
            sx={{ mt: 1 }}
          >
            Add Manually
          </Button>
        )}
    </Box>
  );
};
